import { createContext, useContext, useMemo, useState } from 'react';
import type { ByteInputMode } from '../../shared/analysis';

export interface WorkspaceFile {
  name: string;
  type: string;
  size: number;
  lastModified: number;
  bytes: Uint8Array;
}

interface WorkspaceState {
  input: string;
  output: string;
  activeTool: string;
  inputMode: ByteInputMode;
  file: WorkspaceFile | null;
  setInput: (next: string) => void;
  setInputMode: (next: ByteInputMode) => void;
  setFile: (file: File) => Promise<void>;
  setOutput: (next: string) => void;
  setActiveTool: (next: string) => void;
  clear: () => void;
}

const WorkspaceContext = createContext<WorkspaceState | null>(null);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [inputMode, setInputMode] = useState<ByteInputMode>('auto');
  const [file, setFileState] = useState<WorkspaceFile | null>(null);
  const [activeTool, setActiveTool] = useState(localStorage.getItem('hexyr:lastTool') ?? 'inspect');

  const value = useMemo(
    () => ({
      input,
      output,
      activeTool,
      inputMode,
      file,
      setInput: (next: string) => {
        setInput(next);
        setFileState(null);
        if (inputMode === 'file') setInputMode('auto');
      },
      setInputMode,
      setFile: async (nextFile: File) => {
        const bytes = new Uint8Array(await nextFile.arrayBuffer());
        setFileState({
          name: nextFile.name,
          type: nextFile.type,
          size: nextFile.size,
          lastModified: nextFile.lastModified,
          bytes,
        });
        setInput('');
        setOutput('');
        setInputMode('file');
      },
      setOutput,
      setActiveTool: (next: string) => {
        setActiveTool(next);
        localStorage.setItem('hexyr:lastTool', next);
      },
      clear: () => {
        setInput('');
        setOutput('');
        setFileState(null);
        setInputMode('auto');
      },
    }),
    [activeTool, file, input, inputMode, output],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceState {
  const value = useContext(WorkspaceContext);
  if (!value) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return value;
}
